import { FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
    BaseModal,
    InputField,
    LoadingContainer,
    PrimaryButton,
    SecondaryButton,
    Text,
} from '@tuhi/components'
import { selectAuthState } from 'app/store/selectors'
import { closeModal } from 'app/store/slices/modalSlice'
import {
    AccSubmissionResult,
    accFieldLabel,
    isPlausibleClaimReference,
    normaliseClaimReference,
    submitFormToAcc,
} from 'app/api/forms/services/accSubmissionService'

// padding-top, not margin-top: BaseModal sizes its scroll area from the inner
// wrapper's measured content height, which a collapsed child margin escapes,
// so a margin here left the scroll area 17px short and clipped the buttons.
const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-top: 17px;
`

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const Actions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
`

const Loading = styled.div`
    display: flex;
    justify-content: center;
    padding: 24px 0;
`

const IssueList = styled.ul`
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`

interface Props {
    formId: string
    /** Shown in the intro line, e.g. "ACC 45 for Jane Doe". */
    formLabel?: string
}

/**
 * ACA-16: "Send to ACC" for a completed form. Collects the ACC claim number
 * and the ACC document type, then calls the backend, which renders the same
 * PDF clinicians download and uploads it with the patient details from the
 * consult. Nothing is sent if a mandatory field is missing.
 *
 * The document type is free text until ACC confirms the valid `contentType`
 * codes (question 3 to Jon on ACA-16); it becomes a picker, or a per-form
 * setting, once the list is known.
 */
export const SendToAccModal: FC<Props> = ({ formId, formLabel }) => {
    const dispatch = useDispatch()
    const { msalCredentials } = useSelector(selectAuthState)
    const accessToken = msalCredentials?.accessToken ?? null

    const [claimNumber, setClaimNumber] = useState('')
    const [contentType, setContentType] = useState('')
    const [claimTouched, setClaimTouched] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [result, setResult] = useState<AccSubmissionResult | null>(null)

    const normalisedClaim = normaliseClaimReference(claimNumber)
    const claimInvalid =
        normalisedClaim.length > 0 &&
        !isPlausibleClaimReference(normalisedClaim)
    const canSend =
        !!accessToken &&
        normalisedClaim.length > 0 &&
        !claimInvalid &&
        contentType.trim().length > 0 &&
        !isSending

    const dismiss = () => {
        dispatch(closeModal())
    }

    const send = async () => {
        if (!canSend || !accessToken) return
        setIsSending(true)
        setResult(null)
        const outcome = await submitFormToAcc({
            accessToken,
            formId,
            claimReferenceNumber: normalisedClaim,
            contentType,
        })
        setResult(outcome)
        setIsSending(false)
    }

    if (isSending) {
        return (
            <BaseModal
                title="Send to ACC"
                closeButtonOnClick={dismiss}
                width="large"
            >
                <Loading>
                    <LoadingContainer text="Sending to ACC..." isLoading />
                </Loading>
            </BaseModal>
        )
    }

    if (result?.kind === 'receipted') {
        return (
            <BaseModal
                title="Send to ACC"
                closeButtonOnClick={dismiss}
                width="large"
            >
                <Content>
                    <Text size="medium" $weight="regular" role="status">
                        ACC has received the form for claim {normalisedClaim}.
                    </Text>
                    {result.mode === 'fake' && (
                        <Text size="small" $weight="regular" color="secondary">
                            Test environment: this went to a simulated ACC
                            service. Nothing was sent to ACC.
                        </Text>
                    )}
                    <Actions>
                        <PrimaryButton
                            height="small"
                            width="medium"
                            onClick={dismiss}
                        >
                            Close
                        </PrimaryButton>
                    </Actions>
                </Content>
            </BaseModal>
        )
    }

    const issues = (() => {
        if (!result) return null
        if (result.kind === 'invalid') {
            return (
                <>
                    <Text size="small" $weight="medium">
                        Nothing was sent. ACC needs these details:
                    </Text>
                    <IssueList>
                        {result.missing.map((field) => (
                            <li key={`m-${field}`}>
                                <Text size="small" $weight="regular">
                                    {accFieldLabel(field)} is missing
                                </Text>
                            </li>
                        ))}
                        {result.invalid.map((item) => (
                            <li key={`i-${item.field}`}>
                                <Text size="small" $weight="regular">
                                    {accFieldLabel(item.field)}: {item.reason}
                                </Text>
                            </li>
                        ))}
                    </IssueList>
                    {result.missing.some((f) => f.startsWith('client')) && (
                        <Text size="small" $weight="regular" color="secondary">
                            Attach the patient to this consult, then try again.
                        </Text>
                    )}
                </>
            )
        }
        if (result.kind === 'rejected') {
            return (
                <>
                    <Text size="small" $weight="medium">
                        ACC did not accept the form.
                        {result.message ? ` ${result.message}` : ''}
                    </Text>
                    {result.fields.length > 0 && (
                        <IssueList>
                            {result.fields.map((item, index) => (
                                <li key={`r-${item.field ?? index}`}>
                                    <Text size="small" $weight="regular">
                                        {accFieldLabel(item.field)}
                                        {item.message
                                            ? `: ${item.message}`
                                            : ''}
                                    </Text>
                                </li>
                            ))}
                        </IssueList>
                    )}
                </>
            )
        }
        if (result.kind === 'error') {
            return (
                <Text size="small" $weight="medium">
                    {result.message}
                </Text>
            )
        }
        return null
    })()

    return (
        <BaseModal
            title="Send to ACC"
            closeButtonOnClick={dismiss}
            width="large"
        >
            <Content>
                <Text size="small" $weight="regular">
                    {formLabel
                        ? `Send the PDF of ${formLabel} to ACC.`
                        : 'Send the PDF of this form to ACC.'}{' '}
                    The patient's name, date of birth and NHI are taken from the
                    consult.
                </Text>

                <Fields>
                    <InputField
                        labels={{
                            label: 'ACC claim number',
                            description: 'For example AC344433',
                        }}
                        value={claimNumber}
                        placeholder="Claim number"
                        width="fill"
                        error={claimTouched && claimInvalid}
                        infoMessage={
                            claimTouched && claimInvalid
                                ? {
                                      message:
                                          'This does not look like an ACC claim number: 2 to 4 letters followed by digits.',
                                      isError: true,
                                  }
                                : undefined
                        }
                        onChange={(text) => {
                            setClaimNumber(text)
                            setClaimTouched(true)
                        }}
                        onEnter={send}
                    />
                    <InputField
                        labels={{
                            label: 'ACC document type',
                            description:
                                'The ACC content type code for this form',
                        }}
                        value={contentType}
                        placeholder="Document type code"
                        width="fill"
                        onChange={setContentType}
                        onEnter={send}
                    />
                </Fields>

                {!accessToken && (
                    <Text size="small" $weight="medium" role="alert">
                        Your session has expired. Please log in again to send to
                        ACC.
                    </Text>
                )}

                {issues && <div role="alert">{issues}</div>}

                <Actions>
                    <SecondaryButton
                        height="small"
                        width="hug"
                        onClick={dismiss}
                    >
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        height="small"
                        width="medium"
                        onClick={send}
                        disabled={!canSend}
                    >
                        Send to ACC
                    </PrimaryButton>
                </Actions>
            </Content>
        </BaseModal>
    )
}
